import '../../i18n/autoTranslate';
import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import * as LucideIcons from "lucide-react";
import { CalendarDays } from "lucide-react";
import Button from "../../../components/ui/Button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../../components/ui/shadcn/dialog";
import { Input } from "../../../components/ui/shadcn/input";
import { Textarea } from "../../../components/ui/shadcn/textarea";
import { Switch } from "../../../components/ui/shadcn/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../../../components/ui/shadcn/select";
import { createSessionAPI } from "../../../features/upcoming_sessions/upcomingSessionAPI";

const toLocalDateTimeInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fromLocalDateTimeInputToIso = (value) => {
  if (!value) return "";
  const d = new Date(value);
  return d.toISOString();
};

const hexToRgba = (hex, alpha = 0.18) => {
  const raw = String(hex || "").replace("#", "");
  if (raw.length !== 6) return `rgba(139, 92, 246, ${alpha})`;
  const r = parseInt(raw.slice(0, 2), 16);
  const g = parseInt(raw.slice(2, 4), 16);
  const b = parseInt(raw.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const mapDifficultyLabel = (aiDifficulty) => {
  const v = Number(aiDifficulty || 3);
  if (v <= 2) return "Easy";
  if (v === 3) return "Medium";
  return "Hard";
};

const toTitleCase = (s) => {
  const v = String(s || "").trim();
  if (!v) return "";
  return v.charAt(0).toUpperCase() + v.slice(1);
};

export default function ScheduleSessionDialog({
  open,
  onOpenChange,
  initialScheduledTime,
  defaultDuration = 60,
  onCreated
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [iconQuery, setIconQuery] = useState("Calendar");

  const [form, setForm] = useState({
    title: "",
    company: "",
    scheduled_time: "",
    duration: defaultDuration,
    icon: "CalendarDays",
    color: "#8B7CFF",
    interview: {
      selected_format: "video",
      interview_type: "behavioral",
      selected_industry: "",
      selected_role: "",
      selected_company_type: "",
      selected_companies: "",
      selected_categories: "",
      custom_questions: "",
      enable_follow_ups: true,
      surprise_questions: false,
      star_method_enforcement: false,
      follow_up_intensity: 3,
      adaptive_difficulty: false,
      ai_persona_name: "Nova",
      ai_accent: "american",
      ai_tone: 3,
      ai_difficulty: 3,
      ai_pace: 3,
      ai_pressure: 3
    }
  });

  const scheduledTimeLocal = useMemo(() => toLocalDateTimeInput(initialScheduledTime), [initialScheduledTime]);

  useEffect(() => {
    if (!open) return;
    setError("");
    setForm((prev) => ({
      ...prev,
      scheduled_time: prev.scheduled_time || scheduledTimeLocal,
      duration: prev.duration || defaultDuration
    }));
  }, [open, scheduledTimeLocal, defaultDuration]);

  const update = (key, value) => setForm((p) => ({ ...p, [key]: value }));
  const updateInterview = (key, value) => setForm((p) => ({ ...p, interview: { ...p.interview, [key]: value } }));

  const submit = async () => {
    setSaving(true);
    setError("");
    try {
      const difficulty = mapDifficultyLabel(form.interview.ai_difficulty);
      const type = toTitleCase(form.interview.interview_type) || "Interview";

      const payload = {
        title: String(form.title || "").trim(),
        company: String(form.company || "").trim(),
        type,
        scheduled_time: fromLocalDateTimeInputToIso(form.scheduled_time),
        duration: Number(form.duration || 0),
        difficulty,
        icon: String(form.icon || "CalendarDays"),
        spotlight: hexToRgba(form.color, 0.18),
        interview: {
          selected_format: form.interview.selected_format,
          duration: Number(form.duration || 0),
          interview_type: form.interview.interview_type,
          selected_industry: String(form.interview.selected_industry || "").trim(),
          selected_role: String(form.interview.selected_role || "").trim(),
          selected_company_type: String(form.interview.selected_company_type || "").trim(),
          selected_companies: String(form.interview.selected_companies || "")
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean),
          selected_categories: String(form.interview.selected_categories || "")
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean),
          custom_questions: String(form.interview.custom_questions || "")
            .split("\n")
            .map((s) => s.trim())
            .filter(Boolean),
          enable_follow_ups: Boolean(form.interview.enable_follow_ups),
          surprise_questions: Boolean(form.interview.surprise_questions),
          star_method_enforcement: Boolean(form.interview.star_method_enforcement),
          follow_up_intensity: Number(form.interview.follow_up_intensity || 3),
          adaptive_difficulty: Boolean(form.interview.adaptive_difficulty),
          ai_persona_name: String(form.interview.ai_persona_name || "").trim(),
          ai_accent: String(form.interview.ai_accent || "american"),
          ai_tone: Number(form.interview.ai_tone || 3),
          ai_difficulty: Number(form.interview.ai_difficulty || 3),
          ai_pace: Number(form.interview.ai_pace || 3),
          ai_pressure: Number(form.interview.ai_pressure || 3)
        }
      };

      if (!payload.title || !payload.company || !payload.scheduled_time || !payload.duration) {
        throw new Error("Please fill all required session fields.");
      }

      await createSessionAPI(payload);
      onOpenChange(false);
      if (onCreated) await onCreated();
    } catch (e) {
      setError(e?.message || "Failed to schedule session.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-none w-[calc(100vw-1rem)] sm:w-[min(1400px,calc(100vw-2rem))] max-w-none p-0 bg-[#0B0B12] border-white/10 overflow-hidden"
        showCloseButton={!saving}
      >
        <div className="relative flex flex-col h-[min(90vh,920px)]">
          <div className="absolute inset-0 opacity-30 pointer-events-none">
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 via-purple-500/10 to-cyan-400/5" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.08),transparent_45%),radial-gradient(circle_at_80%_20%,rgba(108,99,255,0.10),transparent_40%),radial-gradient(circle_at_60%_80%,rgba(59,130,246,0.08),transparent_45%)]" />
          </div>

          <div className="relative px-5 sm:px-7 py-5 border-b border-white/10 bg-[#0B0B12]/40 backdrop-blur-xl">
            <DialogHeader className="space-y-1">
              <div className="flex items-start justify-center gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-11 h-11 rounded-2xl bg-gradient-to-br from-[#8B7CFF] to-[#6C63FF] text-white border border-white/10 shadow-[0_0_50px_rgba(108,99,255,0.25)]">
                    <CalendarDays className="h-5 w-5" />
                  </div>
                  <div>
                    <DialogTitle className="text-white text-center">Session Setup</DialogTitle>
                    <DialogDescription className="text-white/60">
                      Create an upcoming session and auto-generate the linked interview.
                    </DialogDescription>
                  </div>
                </div>
              </div>
            </DialogHeader>
          </div>

          <div className="relative flex-1 overflow-auto px-5 sm:px-7 py-6">
            <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-5">
              <div className="space-y-5">
                <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-white/8 to-white/3 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-sm font-semibold text-white">Session</div>
                    <div className="text-xs text-white/55">Required</div>
                  </div>
                  <div className="grid grid-cols-1 gap-3">
                    <div className="space-y-2 sm:col-span-2">
                      <div className="text-sm text-white/70">Title</div>
                      <Input value={form.title} onChange={(e) => update("title", e.target.value)} className="bg-white/5 border-white/10 text-white" />
                    </div>
                    <div className="space-y-2 sm:col-span-2">
                      <div className="text-sm text-white/70">Company</div>
                      <Input value={form.company} onChange={(e) => update("company", e.target.value)} className="bg-white/5 border-white/10 text-white" />
                    </div>
                    <div className="space-y-2 sm:col-span-2">
                      <div className="text-sm text-white/70">Scheduled Time</div>
                      <Input
                        type="datetime-local"
                        value={form.scheduled_time}
                        onChange={(e) => update("scheduled_time", e.target.value)}
                        className="bg-white/5 border-white/10 text-white"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <div className="text-sm text-white/70">Duration</div>
                        <Input
                          type="number"
                          min={15}
                          step={5}
                          value={form.duration}
                          onChange={(e) => update("duration", Number(e.target.value))}
                          className="bg-white/5 border-white/10 text-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <div className="text-sm text-white/70">Color</div>
                        <div className="flex items-center gap-3">
                          <Input
                            type="color"
                            value={form.color}
                            onChange={(e) => update("color", e.target.value)}
                            className="h-10 w-14 p-1 bg-white/5 border-white/10"
                          />
                          <div className="flex-1 rounded-2xl border border-white/10 bg-[#0B0B12]/35 px-3 py-2">
                            <div className="text-[11px] text-white/55">Calendar tint</div>
                            <div className="text-xs text-white/80 font-mono truncate">{form.color}</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="sm:col-span-2 grid grid-cols-2 gap-3">
                      <div className="rounded-2xl border border-white/10 bg-[#0B0B12]/30 px-3 py-2">
                        <div className="text-xs text-white/60">Type (auto)</div>
                        <div className="text-sm text-white/80">{toTitleCase(form.interview.interview_type) || "Interview"}</div>
                      </div>
                      <div className="rounded-2xl border border-white/10 bg-[#0B0B12]/30 px-3 py-2">
                        <div className="text-xs text-white/60">Difficulty (auto)</div>
                        <div className="text-sm text-white/80">{mapDifficultyLabel(form.interview.ai_difficulty)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-5">
                <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-white/8 to-white/3 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-sm font-semibold text-white">Interview Setup</div>
                    <div className="text-xs text-white/55">Auto-saves to InterviewSession</div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <div className="text-sm text-white/70">Session Format</div>
                      <Select value={form.interview.selected_format} onValueChange={(v) => updateInterview("selected_format", v)}>
                        <SelectTrigger className="w-full bg-white/5 border-white/10 text-white">
                          <SelectValue placeholder="Format" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0B0B12] border-white/10 text-white">
                          <SelectItem value="video">Video</SelectItem>
                          <SelectItem value="phone">Phone</SelectItem>
                          <SelectItem value="in_person">In Person</SelectItem>
                          <SelectItem value="chat">Chat</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <div className="text-sm text-white/70">Interview Type</div>
                      <Select value={form.interview.interview_type} onValueChange={(v) => updateInterview("interview_type", v)}>
                        <SelectTrigger className="w-full bg-white/5 border-white/10 text-white">
                          <SelectValue placeholder="Interview type" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0B0B12] border-white/10 text-white">
                          <SelectItem value="behavioral">Behavioral</SelectItem>
                          <SelectItem value="technical">Technical</SelectItem>
                          <SelectItem value="panel">Panel</SelectItem>
                          <SelectItem value="stress">Stress</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <div className="text-sm text-white/70">Industry</div>
                      <Input value={form.interview.selected_industry} onChange={(e) => updateInterview("selected_industry", e.target.value)} className="bg-white/5 border-white/10 text-white" />
                    </div>

                    <div className="space-y-2">
                      <div className="text-sm text-white/70">Role</div>
                      <Input value={form.interview.selected_role} onChange={(e) => updateInterview("selected_role", e.target.value)} className="bg-white/5 border-white/10 text-white" />
                    </div>

                    <div className="space-y-2">
                      <div className="text-sm text-white/70">Company Type</div>
                      <Input value={form.interview.selected_company_type} onChange={(e) => updateInterview("selected_company_type", e.target.value)} className="bg-white/5 border-white/10 text-white" />
                    </div>

                    <div className="space-y-2">
                      <div className="text-sm text-white/70">Companies (comma separated)</div>
                      <Input value={form.interview.selected_companies} onChange={(e) => updateInterview("selected_companies", e.target.value)} className="bg-white/5 border-white/10 text-white" />
                    </div>

                    <div className="space-y-2 sm:col-span-2">
                      <div className="text-sm text-white/70">Categories (comma separated)</div>
                      <Input value={form.interview.selected_categories} onChange={(e) => updateInterview("selected_categories", e.target.value)} className="bg-white/5 border-white/10 text-white" />
                    </div>

                    <div className="space-y-2 sm:col-span-2">
                      <div className="text-sm text-white/70">Custom Questions (one per line)</div>
                      <Textarea value={form.interview.custom_questions} onChange={(e) => updateInterview("custom_questions", e.target.value)} className="bg-white/5 border-white/10 text-white min-h-[110px]" />
                    </div>

                    <div className="rounded-2xl border border-white/10 bg-[#0B0B12]/30 p-4 sm:col-span-2">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-sm text-white/70">Follow-ups</div>
                          <Switch checked={form.interview.enable_follow_ups} onCheckedChange={(v) => updateInterview("enable_follow_ups", v)} />
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-sm text-white/70">Surprise</div>
                          <Switch checked={form.interview.surprise_questions} onCheckedChange={(v) => updateInterview("surprise_questions", v)} />
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-sm text-white/70">STAR Guidance</div>
                          <Switch checked={form.interview.star_method_enforcement} onCheckedChange={(v) => updateInterview("star_method_enforcement", v)} />
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-sm text-white/70">Adaptive Difficulty</div>
                          <Switch checked={form.interview.adaptive_difficulty} onCheckedChange={(v) => updateInterview("adaptive_difficulty", v)} />
                        </div>
                      </div>
                      <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3">
                        <div className="space-y-2 md:col-span-2">
                          <div className="text-sm text-white/70">AI Persona</div>
                          <Input value={form.interview.ai_persona_name} onChange={(e) => updateInterview("ai_persona_name", e.target.value)} className="bg-white/5 border-white/10 text-white" />
                        </div>
                        <div className="space-y-2">
                          <div className="text-sm text-white/70">Tone</div>
                          <Input type="number" min={1} max={5} value={form.interview.ai_tone} onChange={(e) => updateInterview("ai_tone", Number(e.target.value))} className="bg-white/5 border-white/10 text-white" />
                        </div>
                        <div className="space-y-2">
                          <div className="text-sm text-white/70">Difficulty</div>
                          <Input type="number" min={1} max={5} value={form.interview.ai_difficulty} onChange={(e) => updateInterview("ai_difficulty", Number(e.target.value))} className="bg-white/5 border-white/10 text-white" />
                        </div>
                        <div className="space-y-2">
                          <div className="text-sm text-white/70">Pressure</div>
                          <Input type="number" min={1} max={5} value={form.interview.ai_pressure} onChange={(e) => updateInterview("ai_pressure", Number(e.target.value))} className="bg-white/5 border-white/10 text-white" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-white/8 to-white/3 p-4">
                    <div className="flex items-center justify-between mb-3">
                    <div className="text-sm font-semibold text-white">Icon</div>
                      <div className="text-xs text-white/55">Pick an icon</div>
                  </div>
                  <div className="space-y-3">
                    <Input
                      value={iconQuery}
                      onChange={(e) => setIconQuery(e.target.value)}
                      className="bg-white/5 border-white/10 text-white w-full"
                        placeholder="Search icons (e.g., Calendar, Sparkles, Briefcase)"
                    />

                    <IconPicker
                      query={iconQuery}
                      value={form.icon}
                      onChange={(v) => update("icon", v)}
                      accentColor={form.color}
                    />
                  </div>
                </div>
              </div>
            </div>

            {error && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4 rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-200"
              >
                {error}
              </motion.div>
            )}
          </div>

          <div className="relative px-5 sm:px-7 py-5 border-t border-white/10 bg-[#0B0B12]/55 backdrop-blur-xl">
            <div className="flex flex-col sm:flex-row gap-3 sm:justify-between sm:items-center">
              <div className="text-xs text-white/55">
                Type and difficulty are derived from interview settings. Color is used for the session spotlight.
              </div>
              <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
                <Button
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={saving}
                  className="w-full sm:w-auto border-white/10 bg-white/5 hover:bg-white/10 text-white"
                >
                  Cancel
                </Button>
                <Button
                  onClick={submit}
                  loading={saving}
                  iconName="ArrowRight"
                  iconPosition="right"
                  size="lg"
                  className="w-full sm:w-auto"
                >
                  {saving ? "Scheduling..." : "Schedule Session"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function IconPicker({ query, value, onChange, accentColor }) {
  const results = useMemo(() => {
    const q = String(query || "").trim().toLowerCase();
    const keys = Object.keys(LucideIcons).filter((k) => {
      const v = LucideIcons[k];
      const okComponent = typeof v === "function" || (typeof v === "object" && v && "$$typeof" in v);
      const okName = /^[A-Z]/.test(k);
      if (!okComponent || !okName) return false;
      if (!q) return k === "CalendarDays" || k === "Calendar" || k === "Sparkles" || k === "Briefcase" || k === "Target" || k === "Rocket" || k === "Video";
      return k.toLowerCase().includes(q);
    });
    return keys.slice(0, 24);
  }, [query]);

  return (
    <div className="space-y-2">
      {results.length === 0 && (
        <div className="text-sm text-white/60">
          No icons found. Try a different keyword (example: Calendar, Video, Star).
        </div>
      )}
      <div role="radiogroup" aria-label="Icon picker" className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
        {results.map((name) => {
          const Icon = LucideIcons[name];
          const selected = value === name;
          return (
            <button
              key={name}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onChange && onChange(name)}
              className={`group rounded-2xl border px-2 py-2 transition-colors ${
                selected ? "border-white/20 bg-white/10" : "border-white/10 bg-white/5 hover:bg-white/10"
              }`}
              style={selected ? { boxShadow: `0 0 30px ${hexToRgba(accentColor, 0.25)}` } : undefined}
              title={name}
            >
              <div className="flex items-center justify-center">
                {Icon ? (
                  <Icon className={`h-5 w-5 ${selected ? "text-white" : "text-white/70 group-hover:text-white"}`} />
                ) : (
                  <CalendarDays className={`h-5 w-5 ${selected ? "text-white" : "text-white/70 group-hover:text-white"}`} />
                )}
              </div>
              <div className="mt-1 text-[10px] text-white/55 text-center truncate">{name}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

